import React, { useState, useRef, useCallback } from 'react';
import { Modal } from '@fluentui/react';
import { 
  ZoomIn24Regular, 
  ZoomOut24Regular, 
  ArrowDownload24Regular,
  Dismiss24Regular,
  FullScreenMaximize24Regular
} from '@fluentui/react-icons';
import styles from './ArchitectureDiagram.module.css';

interface ArchitectureDiagramProps {
  src: string;
  alt: string;
  title?: string;
}

export const ArchitectureDiagram: React.FC<ArchitectureDiagramProps> = ({ 
  src, 
  alt,
  title 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoom, setZoom] = useState(100);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDownload = useCallback(() => {
    const link = document.createElement('a');
    link.href = src;
    link.download = `${title || 'architecture-diagram'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [src, title]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 10, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 10, 10));

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -5 : 5;
      setZoom(prev => Math.min(Math.max(prev + delta, 10), 200));
    }
  }, []);

  const openModal = () => {
    setZoom(50);  // Start at 50% for better initial view
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      {/* Thumbnail View */}
      <div className={styles.diagramContainer}>
        <div className={styles.diagramHeader}>
          <span className={styles.diagramTitle}>{title || 'Architecture Diagram'}</span>
          <div className={styles.headerButtons}>
            <button 
              className={styles.iconBtn} 
              onClick={openModal}
              title="Expand"
              type="button"
            >
              <FullScreenMaximize24Regular />
            </button>
            <button 
              className={styles.iconBtn} 
              onClick={handleDownload}
              title="Download"
              type="button"
            >
              <ArrowDownload24Regular />
            </button>
          </div>
        </div>
        <div className={styles.thumbnailWrapper} onClick={openModal}>
          <img 
            src={src} 
            alt={alt} 
            className={styles.thumbnailImage}
          />
          <div className={styles.expandOverlay}>
            <FullScreenMaximize24Regular />
            <span>Click to expand</span>
          </div>
        </div>
      </div>

      {/* Full Screen Modal */}
      <Modal
        isOpen={isModalOpen}
        onDismiss={closeModal}
        isBlocking={false}
        containerClassName={styles.modalContainer}
      >
        <div className={styles.modalContent}>
          {/* Modal Header */}
          <div className={styles.modalHeader}>
            <span className={styles.modalTitle}>{title || 'Architecture Diagram'}</span>
            <div className={styles.modalControls}>
              <button 
                className={styles.iconBtn} 
                onClick={handleZoomOut} 
                disabled={zoom <= 10}
                title="Zoom out"
                type="button"
              >
                <ZoomOut24Regular />
              </button>
              <div className={styles.zoomDisplay}>
                <input
                  type="range"
                  min="10"
                  max="200"
                  step="5"
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className={styles.zoomSlider}
                />
                <span className={styles.zoomValue}>{zoom}%</span>
              </div>
              <button 
                className={styles.iconBtn} 
                onClick={handleZoomIn} 
                disabled={zoom >= 200}
                title="Zoom in"
                type="button"
              >
                <ZoomIn24Regular />
              </button>
              <div className={styles.divider} />
              <button 
                className={styles.iconBtn} 
                onClick={handleDownload}
                title="Download"
                type="button"
              >
                <ArrowDownload24Regular />
              </button>
              <button 
                className={styles.iconBtn} 
                onClick={closeModal}
                title="Close"
                type="button"
              >
                <Dismiss24Regular />
              </button>
            </div>
          </div>

          {/* Zoomable Image Area */}
          <div 
            ref={containerRef}
            className={styles.imageViewport}
            onWheel={handleWheel}
          >
            <div 
              className={styles.imageWrapper}
              style={{ 
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'center center'
              }}
            >
              <img 
                ref={imageRef}
                src={src} 
                alt={alt}
                className={styles.fullImage}
                draggable={false}
              />
            </div>
          </div>

          {/* Footer hint */}
          <div className={styles.modalFooter}>
            <span>Use Ctrl/Cmd + scroll to zoom</span>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ArchitectureDiagram;
